import { readSfxEnabled } from './preferences'

export type LawnSpaceSfxName = 'shoot' | 'hit' | 'destroy' | 'powerup' | 'damage' | 'wave-clear'

type AudioContextCtor = typeof AudioContext

let ctx: AudioContext | null = null
let masterGain: GainNode | null = null
let unlocked = false

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (ctx) return ctx
  const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext) as AudioContextCtor | undefined
  if (!Ctor) return null
  try {
    ctx = new Ctor()
    masterGain = ctx.createGain()
    masterGain.gain.value = 0.55
    masterGain.connect(ctx.destination)
    return ctx
  } catch {
    return null
  }
}

export function unlockLawnSpaceAudio() {
  if (unlocked) return
  const audio = getAudioContext()
  if (!audio) return
  if (audio.state === 'suspended') {
    audio.resume().catch(() => {})
  }
  unlocked = true
}

function envelope(
  gain: GainNode,
  start: number,
  duration: number,
  peak: number,
) {
  gain.gain.cancelScheduledValues(start)
  gain.gain.setValueAtTime(0, start)
  gain.gain.linearRampToValueAtTime(peak, start + Math.min(0.008, duration / 4))
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
}

function tone(
  type: OscillatorType,
  freq: number | [number, number],
  duration: number,
  peak: number,
  startOffset = 0,
) {
  const audio = getAudioContext()
  if (!audio || !masterGain) return
  const t = audio.currentTime + startOffset
  const osc = audio.createOscillator()
  const gain = audio.createGain()
  osc.type = type
  if (Array.isArray(freq)) {
    osc.frequency.setValueAtTime(freq[0], t)
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, freq[1]), t + duration)
  } else {
    osc.frequency.setValueAtTime(freq, t)
  }
  osc.connect(gain)
  gain.connect(masterGain)
  envelope(gain, t, duration, peak)
  osc.start(t)
  osc.stop(t + duration + 0.02)
}

function noiseBurst(duration: number, peak: number, filterFreq: number) {
  const audio = getAudioContext()
  if (!audio || !masterGain) return
  const t = audio.currentTime
  const bufferSize = Math.max(1, Math.floor(audio.sampleRate * duration))
  const buffer = audio.createBuffer(1, bufferSize, audio.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < bufferSize; i += 1) {
    data[i] = Math.random() * 2 - 1
  }
  const src = audio.createBufferSource()
  src.buffer = buffer
  const filter = audio.createBiquadFilter()
  filter.type = 'bandpass'
  filter.frequency.value = filterFreq
  filter.Q.value = 0.7
  const gain = audio.createGain()
  src.connect(filter)
  filter.connect(gain)
  gain.connect(masterGain)
  envelope(gain, t, duration, peak)
  src.start(t)
  src.stop(t + duration + 0.02)
}

export function playLawnSpaceSfx(name: LawnSpaceSfxName) {
  if (!readSfxEnabled()) return
  const audio = getAudioContext()
  if (!audio) return
  if (audio.state === 'suspended') audio.resume().catch(() => {})

  switch (name) {
    case 'shoot':
      tone('square', [880, 540], 0.07, 0.18)
      return
    case 'hit':
      noiseBurst(0.05, 0.2, 1800)
      return
    case 'destroy':
      tone('sawtooth', [520, 110], 0.18, 0.28)
      noiseBurst(0.12, 0.12, 600)
      return
    case 'powerup': {
      const notes = [523.25, 659.25, 783.99]
      notes.forEach((freq, i) => tone('triangle', freq, 0.12, 0.22, i * 0.06))
      return
    }
    case 'damage':
      tone('sawtooth', [220, 90], 0.22, 0.3)
      return
    case 'wave-clear': {
      const notes = [659.25, 783.99, 987.77, 1318.51]
      notes.forEach((freq, i) => tone('triangle', freq, 0.18, 0.22, i * 0.08))
      return
    }
  }
}

export function setLawnSpaceMasterVolume(volume: number) {
  if (!masterGain) return
  masterGain.gain.value = Math.max(0, Math.min(1, volume))
}
